/**
 * airan-ntn — Orbital Mechanics
 * Satellite orbital propagation, Walker Delta constellation, ISL geometry
 */

import {
  Vec3, OrbitalElements, Satellite, OrbitType, ISLLink, GroundStation,
  SimConfig, SatelliteState
} from './types.js';
import {
  EARTH_RADIUS, EARTH_MU, ORBIT_DEFAULTS, WALKER_DELTA_PRESETS,
  SAT_COLORS, SPEED_OF_LIGHT
} from './config.js';
import { vec3Add, vec3Sub, vec3Scale, vec3Len, vec3Norm, vec3Dot, vec3Cross } from './types.js';

// ──────────────────────────────────────────────────────────────────────────────
// Coordinate Transformations
// ──────────────────────────────────────────────────────────────────────────────

/** Convert ECEF to Lat/Lon/Alt (degrees, degrees, meters) */
export function ecefToLla(ecef: Vec3): { lat: number; lon: number; alt: number } {
  const { x, y, z } = ecef;
  const p = Math.sqrt(x * x + y * y);
  const lat = Math.atan2(z, p) * 180 / Math.PI;
  const lon = Math.atan2(y, x) * 180 / Math.PI;
  const alt = p / Math.cos(lat * Math.PI / 180) - EARTH_RADIUS;
  return { lat, lon, alt };
}

/** Convert Lat/Lon/Alt to ECEF */
export function llaToEcef(lat: number, lon: number, alt: number): Vec3 {
  const latRad = lat * Math.PI / 180;
  const lonRad = lon * Math.PI / 180;
  const N = EARTH_RADIUS / Math.sqrt(1 - Math.sin(latRad) ** 2); // Simplified
  const x = (N + alt) * Math.cos(latRad) * Math.cos(lonRad);
  const y = (N + alt) * Math.cos(latRad) * Math.sin(lonRad);
  const z = (N * (1 - 0.00669438) + alt) * Math.sin(latRad); // WGS84 eccentricity
  return { x, y, z };
}

/** Convert orbital elements to ECEF position/velocity */
export function orbitalToEcef(elements: OrbitalElements, time: number): { pos: Vec3; vel: Vec3 } {
  const { semiMajorAxis, eccentricity, inclination, raan, argPerigee, trueAnomaly, epoch } = elements;
  const dt = time - epoch;

  // Mean motion
  const n = Math.sqrt(EARTH_MU / (semiMajorAxis ** 3));
  const M = trueAnomaly + n * dt; // Simplified: mean anomaly ≈ true anomaly for circular

  // For circular orbits (e=0), true anomaly = mean anomaly
  const E = M; // Eccentric anomaly ≈ mean anomaly for e≈0
  const nu = 2 * Math.atan(Math.sqrt((1 + eccentricity) / (1 - eccentricity)) * Math.tan(E / 2));

  // Radius
  const r = semiMajorAxis * (1 - eccentricity * Math.cos(E));

  // Position in orbital plane
  const cosNu = Math.cos(nu);
  const sinNu = Math.sin(nu);
  const xOrb = r * cosNu;
  const yOrb = r * sinNu;
  const zOrb = 0;

  // Velocity in orbital plane
  const vMag = Math.sqrt(EARTH_MU * (2 / r - 1 / semiMajorAxis));
  const vxOrb = -vMag * sinNu / Math.sqrt(1 - eccentricity ** 2);
  const vyOrb = vMag * cosNu / Math.sqrt(1 - eccentricity ** 2);
  const vzOrb = 0;

  // Rotation matrices: R3(-raan) * R1(-inc) * R3(-argPerigee)
  const cosR = Math.cos(raan), sinR = Math.sin(raan);
  const cosI = Math.cos(inclination), sinI = Math.sin(inclination);
  const cosW = Math.cos(argPerigee), sinW = Math.sin(argPerigee);

  // Position rotation
  const x = xOrb * (cosR * cosW - sinR * sinW * cosI) - yOrb * (cosR * sinW + sinR * cosW * cosI);
  const y = xOrb * (sinR * cosW + cosR * sinW * cosI) + yOrb * (cosR * cosW * cosI - sinR * sinW);
  const z = xOrb * (sinW * sinI) + yOrb * (cosW * sinI);

  // Velocity rotation (same matrix)
  const vx = vxOrb * (cosR * cosW - sinR * sinW * cosI) - vyOrb * (cosR * sinW + sinR * cosW * cosI);
  const vy = vxOrb * (sinR * cosW + cosR * sinW * cosI) + vyOrb * (cosR * cosW * cosI - sinR * sinW);
  const vz = vxOrb * (sinW * sinI) + vyOrb * (cosW * sinI);

  return { pos: { x, y, z }, vel: { x: vx, y: vy, z: vz } };
}

// ──────────────────────────────────────────────────────────────────────────────
// Walker Delta Constellation
// ──────────────────────────────────────────────────────────────────────────────

export interface WalkerDeltaParams {
  numOrbitals: number;   // Satellites per plane (t)
  numPlanes: number;     // Number of planes (p)
  inclination: number;   // Degrees
  altitude: number;      // km
  phasing: number;       // Phasing parameter (f)
}

export function createWalkerConstellation(params: WalkerDeltaParams, startTime: number): OrbitalElements[] {
  const { numOrbitals, numPlanes, inclination, altitude, phasing = 1 } = params;
  const totalSats = numOrbitals * numPlanes;
  const semiMajorAxis = EARTH_RADIUS + altitude * 1000;
  const incRad = inclination * Math.PI / 180;
  const elements: OrbitalElements[] = [];

  for (let plane = 0; plane < numPlanes; plane++) {
    const raan = (2 * Math.PI * plane) / numPlanes;

    for (let sat = 0; sat < numOrbitals; sat++) {
      const satIdx = plane * numOrbitals + sat;

      // True anomaly with phasing
      const baseAnomaly = (2 * Math.PI * sat) / numOrbitals;
      const phaseOffset = (2 * Math.PI * phasing * plane) / totalSats;
      const trueAnomaly = baseAnomaly + phaseOffset;

      // Argument of perigee (for circular orbits, arbitrary but consistent)
      const argPerigee = 0;

      elements.push({
        semiMajorAxis,
        eccentricity: 0,
        inclination: incRad,
        raan,
        argPerigee,
        trueAnomaly,
        epoch: startTime
      });
    }
  }

  return elements;
}

export function createConstellationFromPreset(presetName: keyof typeof WALKER_DELTA_PRESETS, startTime: number): OrbitalElements[] {
  const preset = WALKER_DELTA_PRESETS[presetName];
  return createWalkerConstellation({
    numOrbitals: preset.numOrbitals,
    numPlanes: preset.numPlanes,
    inclination: preset.inclination,
    altitude: preset.altitude,
    phasing: 1
  }, startTime);
}

// ──────────────────────────────────────────────────────────────────────────────
// Satellite Propagation
// ──────────────────────────────────────────────────────────────────────────────

export function propagateSatellite(sat: Satellite, dt: number): Satellite {
  // Update true anomaly based on mean motion
  const n = Math.sqrt(EARTH_MU / (sat.orbitalElements.semiMajorAxis ** 3));
  const newAnomaly = (sat.orbitalElements.trueAnomaly + n * dt) % (2 * Math.PI);

  const newElements: OrbitalElements = {
    ...sat.orbitalElements,
    trueAnomaly: newAnomaly,
    epoch: sat.orbitalElements.epoch + dt
  };

  const { pos, vel } = orbitalToEcef(newElements, newElements.epoch);

  // Update trail (keep last 50 positions)
  const newTrail = [...sat.trail, sat.position].slice(-50);

  // Check eclipse (simplified: sun direction fixed along -x)
  const sunDir = { x: -1, y: 0, z: 0 };
  const satToSun = vec3Norm(vec3Sub(sunDir, vec3Scale(pos, 1 / vec3Len(pos))));
  const earthToSat = vec3Norm(pos);
  const inEclipse = vec3Dot(earthToSat, sunDir) < 0 && vec3Len(pos) < EARTH_RADIUS * 1.2; // Simplified

  // Solar power (simplified)
  const solarPower = inEclipse ? 0 : 1500; // Watts

  // Battery SoC update (simplified)
  const powerDraw = 200; // Base power consumption
  const batteryDelta = (solarPower - powerDraw) / 5000 * dt / 3600; // Normalized per hour
  const newSoc = Math.max(0, Math.min(1, sat.batterySoc + batteryDelta));

  // Determine state
  let newState = sat.state;
  if (inEclipse) newState = 'ECLIPSE';
  else if (sat.feederLink === null || !sat.feederLink.active) newState = 'FEEDER_OUTAGE';
  else if (sat.state === 'AUTONOMOUS' && sat.feederLink?.active) newState = 'SYNCING';
  else newState = 'ACTIVE';

  return {
    ...sat,
    orbitalElements: newElements,
    position: pos,
    velocity: vel,
    trail: newTrail,
    batterySoc: newSoc,
    solarPower,
    state: newState as SatelliteState
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// ISL Geometry
// ──────────────────────────────────────────────────────────────────────────────

export function calculateISLLinks(satellites: Satellite[], maxRange: number): Map<number, ISLLink[]> {
  const links = new Map<number, ISLLink[]>();

  for (let i = 0; i < satellites.length; i++) {
    const satA = satellites[i];
    const satLinks: ISLLink[] = [];

    for (let j = 0; j < satellites.length; j++) {
      if (i === j) continue;
      const satB = satellites[j];

      const dist = vec3Len(vec3Sub(satA.position, satB.position));
      if (dist <= maxRange) {
        // Check if Earth blocks the link (line of sight)
        const hasLOS = checkLineOfSight(satA.position, satB.position);
        if (hasLOS) {
          const latency = dist / SPEED_OF_LIGHT;
          const capacity = 10e9; // 10 Gbps for laser, 1 Gbps for RF
          satLinks.push({
            neighborId: satB.id,
            distance: dist,
            latency,
            capacity,
            type: 'laser',
            active: true
          });
        }
      }
    }

    // Sort by distance, keep nearest 4 (typical ISL degree)
    satLinks.sort((a, b) => a.distance - b.distance);
    links.set(satA.id, satLinks.slice(0, 4));
  }

  return links;
}

function checkLineOfSight(posA: Vec3, posB: Vec3): boolean {
  // Check if line segment intersects Earth sphere
  const dir = vec3Norm(vec3Sub(posB, posA));
  const dist = vec3Len(vec3Sub(posB, posA));

  // Closest approach to Earth center
  const t = -vec3Dot(posA, dir);
  if (t < 0 || t > dist) return true; // Closest point outside segment

  const closest = vec3Add(posA, vec3Scale(dir, t));
  return vec3Len(closest) > EARTH_RADIUS;
}

// ──────────────────────────────────────────────────────────────────────────────
// Feeder Link (Satellite to Ground Station)
// ──────────────────────────────────────────────────────────────────────────────

export function updateFeederLinks(
  satellites: Satellite[],
  groundStations: GroundStation[],
  minElevation: number
): void {
  for (const sat of satellites) {
    let bestGs: GroundStation | null = null;
    let bestElevation = -90;

    for (const gs of groundStations) {
      const elevation = calculateElevation(sat.position, gs.position);
      if (elevation > minElevation && elevation > bestElevation) {
        bestElevation = elevation;
        bestGs = gs;
      }
    }

    if (bestGs) {
      const dist = vec3Len(sat.position);
      const slantRange = vec3Len(vec3Sub(sat.position, bestGs.position));
      const latency = slantRange / SPEED_OF_LIGHT;
      const snr = calculateFeederSNR(slatRange, bestElevation);
      const capacity = shannonCapacity(snr) * 400e6; // 400 MHz feeder bandwidth

      sat.feederLink = {
        groundStationId: bestGs.id,
        elevation: bestElevation,
        snr,
        capacity,
        latency,
        active: true,
        lastContact: Date.now() / 1000
      };
      sat.islNeighbors = sat.islNeighbors; // Keep existing
    } else {
      sat.feederLink = null;
    }
  }
}

export function calculateElevation(satPos: Vec3, gsPos: Vec3): number {
  const satToGs = vec3Sub(gsPos, satPos);
  const dist = vec3Len(satToGs);
  const earthRadius = vec3Len(satPos);

  // Elevation from satellite to ground station
  // sin(elevation) = (R_earth / r_sat) * cos(gamma) - sqrt(1 - (R_earth / r_sat)^2 * sin^2(gamma))
  // Simplified: elevation from ground station to satellite
  const gsToSat = vec3Norm(vec3Sub(satPos, gsPos));
  const gsUp = vec3Norm(gsPos);
  const elevationRad = Math.asin(Math.max(-1, Math.min(1, vec3Dot(gsToSat, gsUp))));
  return elevationRad * 180 / Math.PI;
}

function calculateFeederSNR(slantRange: number, elevation: number): number {
  // Simplified feeder link budget
  const freqGHz = 2.6;
  const txPower = 30; // dBm (ground station)
  const rxGain = 40;  // dBi (satellite)
  const gsGain = 50;  // dBi (ground station)

  // Path loss (free space + atmospheric)
  const fspl = 92.45 + 20 * Math.log10(freqGHz) + 20 * Math.log10(slantRange / 1000);
  const atmLoss = 0.5 / Math.sin(elevation * Math.PI / 180); // dB

  const rxPower = txPower + gsGain + rxGain - fspl - atmLoss;
  const noise = -174 + 10 * Math.log10(400e6) + 3; // 400 MHz, 3 dB NF

  return rxPower - noise;
}

function shannonCapacity(snrDb: number): number {
  const snrLin = Math.pow(10, snrDb / 10);
  return Math.log2(1 + snrLin);
}

// ──────────────────────────────────────────────────────────────────────────────
// Ground Station Setup
// ──────────────────────────────────────────────────────────────────────────────

export function createGroundStations(config: SimConfig): GroundStation[] {
  const stations: GroundStation[] = [];

  for (let i = 0; i < config.numGroundStations; i++) {
    const pos = config.gsPositions[i % config.gsPositions.length];
    const ecef = llaToEcef(pos.x, pos.y, 0);

    stations.push({
      id: i,
      name: `GS-${i}`,
      position: ecef,
      lat: pos.x,
      lon: pos.y,
      alt: 0,
      elevationMask: 10,
      antennas: 4,
      txPower: 30,
      connectedSats: []
    });
  }

  return stations;
}

// ──────────────────────────────────────────────────────────────────────────────
// Satellite Creation
// ──────────────────────────────────────────────────────────────────────────────

export function createSatellites(config: SimConfig, startTime: number): Satellite[] {
  const elements = createWalkerConstellation({
    numOrbitals: config.numOrbitals,
    numPlanes: config.numPlanes,
    inclination: config.inclination,
    altitude: config.altitude,
    phasing: 1
  }, startTime);

  const satellites: Satellite[] = [];

  for (let i = 0; i < elements.length; i++) {
    const { pos, vel } = orbitalToEcef(elements[i], startTime);
    const { lat, lon } = ecefToLla(pos);

    satellites.push({
      id: i,
      name: `SAT-${i}`,
      orbitType: config.orbitType,
      orbitalElements: elements[i],
      position: pos,
      velocity: vel,
      txPower: 23,
      antennas: 16,
      carrierFreqGHz: config.carrierFreqGHz,
      bandwidthMHz: config.bandwidthMHz,
      state: 'ACTIVE',
      batterySoc: 1.0,
      solarPower: 1500,
      computeBudget: 10000,
      islNeighbors: [],
      islLinks: [],
      feederLink: null,
      localModel: null,
      localGradients: null,
      flRound: 0,
      color: SAT_COLORS[i % SAT_COLORS.length],
      trail: []
    });
  }

  return satellites;
}

// ──────────────────────────────────────────────────────────────────────────────
// Satellite Visibility for UEs
// ──────────────────────────────────────────────────────────────────────────────

export function getVisibleSatellites(uePos: Vec3, satellites: Satellite[], minElevation: number): Satellite[] {
  const visible: Satellite[] = [];

  for (const sat of satellites) {
    const elevation = calculateUeElevation(uePos, sat.position);
    if (elevation >= minElevation) {
      visible.push(sat);
    }
  }

  // Sort by elevation (highest first)
  visible.sort((a, b) => calculateUeElevation(uePos, b.position) - calculateUeElevation(uePos, a.position));
  return visible;
}

function calculateUeElevation(uePos: Vec3, satPos: Vec3): number {
  const ueToSat = vec3Norm(vec3Sub(satPos, uePos));
  const ueUp = vec3Norm(uePos);
  const elevationRad = Math.asin(Math.max(-1, Math.min(1, vec3Dot(ueToSat, ueUp))));
  return elevationRad * 180 / Math.PI;
}

export function getBestSatellite(uePos: Vec3, satellites: Satellite[], minElevation: number): Satellite | null {
  const visible = getVisibleSatellites(uePos, satellites, minElevation);
  if (visible.length === 0) return null;

  // Best by elevation + SNR (simplified)
  let best = visible[0];
  let bestScore = -Infinity;

  for (const sat of visible) {
    const elevation = calculateUeElevation(uePos, sat.position);
    const dist = vec3Len(vec3Sub(uePos, sat.position));
    const snr = estimateSNR(dist, elevation);
    const score = elevation * 0.5 + snr * 0.5;
    if (score > bestScore) {
      bestScore = score;
      best = sat;
    }
  }

  return best;
}

function estimateSNR(dist: number, elevation: number): number {
  const freqGHz = 2.6;
  const txPower = 23; // dBm
  const fspl = 92.45 + 20 * Math.log10(freqGHz) + 20 * Math.log10(dist / 1000);
  const rxPower = txPower - fspl;
  const noise = -174 + 10 * Math.log10(20e6) + 5; // 20 MHz, 5 dB NF
  return rxPower - noise;
}

// Re-export SPEED_OF_LIGHT for use in other modules
export { SPEED_OF_LIGHT } from './config.js';